/* global SCREENS */
/* eslint-disable object-curly-newline */

import gsap from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';

import $ from '../core/Dom';

gsap.registerPlugin(Draggable, InertiaPlugin);

/**
 * Ecosystem diagram.
 *
 * Below the `l` breakpoint the three columns are a horizontal slider: the
 * viewport clips, and the track inside it is dragged on its transform by GSAP
 * Draggable, throwing to the nearest column on release. Imager X is centred on
 * load, so it reads first and Input/Output are one throw either side.
 *
 * Transform rather than scrollLeft on purpose — Draggable's scroll types wrap
 * the element's children in a generated div, which would break both the card
 * sizing and the measurements here.
 *
 * Arrow keys step between columns, since a clipped, transformed track has no
 * native scrolling for a keyboard to drive.
 */

const KEY_STEP = { ArrowLeft: -1, ArrowRight: 1, Left: -1, Right: 1 };

export default el => {
    const $el = $(el);

    let track = null;
    let focus = null;
    let cards = [];

    // x offsets that centre each card, ordered left to right
    let snapPoints = [];
    let minX = 0;

    let draggable = null;
    let hasMoved = false;
    let query = null;

    const isSlider = () => !!query && !query.matches;

    const getFrameWidth = () => {
        const styles = window.getComputedStyle(el);
        return el.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    };

    /**
     * Card positions are measured relative to the track's own box, so they hold
     * regardless of how far the track is currently translated.
     */
    const measure = () => {
        const frame = getFrameWidth();
        const trackLeft = track.getBoundingClientRect().left;

        let contentWidth = 0;

        snapPoints = cards.map(card => {
            const rect = card.getBoundingClientRect();
            const offset = rect.left - trackLeft;

            contentWidth = Math.max(contentWidth, offset + rect.width);

            // Where the track has to sit for this card to be centred in the frame
            return (frame / 2) - (offset + (rect.width / 2));
        });

        minX = Math.min(0, frame - contentWidth);

        // Never past the ends
        snapPoints = snapPoints.map(x => Math.max(minX, Math.min(0, x)));
    };

    const nearestSnap = x => snapPoints.reduce(
        (closest, point) => (Math.abs(point - x) < Math.abs(closest - x) ? point : closest),
        snapPoints[0]
    );

    const setX = (x, animate) => {
        if (animate) {
            gsap.to(track, { x, duration: 0.5, ease: 'power3.out', overwrite: true });
        } else {
            gsap.set(track, { x });
        }
    };

    const centreFocus = animate => {
        if (hasMoved || !isSlider()) {
            return;
        }

        const index = cards.indexOf(focus);

        if (index !== -1) {
            setX(snapPoints[index], animate);
        }
    };

    const onKeyDown = e => {
        const step = KEY_STEP[e.key];

        if (!step || !isSlider() || !draggable) {
            return;
        }

        e.preventDefault();
        hasMoved = true;

        const current = nearestSnap(gsap.getProperty(track, 'x'));
        const index = snapPoints.indexOf(current);
        const next = snapPoints[Math.max(0, Math.min(index + step, snapPoints.length - 1))];

        setX(next, true);
    };

    /**
     * Once all three columns fit there is nothing to drag, so the track is
     * reset to the grid's own layout and Draggable stands down.
     */
    const sync = () => {
        if (!draggable) {
            return;
        }

        if (isSlider()) {
            measure();
            draggable.enable();
            draggable.applyBounds({ minX, maxX: 0, minY: 0, maxY: 0 });
            el.setAttribute('tabindex', '0');

            if (hasMoved) {
                setX(nearestSnap(gsap.getProperty(track, 'x')), false);
            } else {
                centreFocus(false);
            }
        } else {
            draggable.disable();
            $el.removeClass('is-dragging');
            el.removeAttribute('tabindex');
            gsap.set(track, { clearProps: 'transform' });
        }
    };

    const init = () => {
        track = $el.find('[data-diagram-track]').get(0);

        if (!track) {
            return;
        }

        focus = $el.find('[data-diagram-focus]').get(0);
        cards = $el.find('.ds-diagram__col').get();

        if (!focus || cards.length < 2) {
            return;
        }

        // Mirrors the `l` breakpoint the CSS switches layout at
        query = window.matchMedia(`(min-width: ${SCREENS.l})`);

        measure();

        [draggable] = Draggable.create(track, {
            type: 'x',
            inertia: true,
            bounds: { minX, maxX: 0, minY: 0, maxY: 0 },
            edgeResistance: 0.85,
            dragResistance: 0,
            maxDuration: 0.7,
            // A vertical swipe over the track stays a page scroll
            allowNativeTouchScrolling: true,
            allowContextMenu: true,
            snap: { x: value => nearestSnap(value) },
            onPress() {
                $el.addClass('is-dragging');
            },
            onDragStart() {
                hasMoved = true;
            },
            onRelease() {
                $el.removeClass('is-dragging');
            }
        });

        $el.on('keydown', onKeyDown);

        window.addEventListener('resize', sync);

        sync();
    };

    const destroy = () => {
        if (draggable) {
            draggable.kill();
            draggable = null;
        }

        query = null;
        window.removeEventListener('resize', sync);
        $el.off('keydown');
        $el.removeClass('is-dragging');

        if (track) {
            gsap.set(track, { clearProps: 'transform' });
        }

        track = null;
        focus = null;
        cards = [];
        snapPoints = [];
    };

    return {
        init,
        destroy
    };
};
